import { eventsNetwork, network } from './network';
import { undoOf } from './activityLogApi';
import { PlanningUndo, SessionPlace, TaskUndoReceipt, UndoneTask } from '@state/undo/types';
import { MarkUsedBlock, MarkUsedResponse, UnplanResponse } from './plannerApi';
import { SourceEvent } from '@state/events/types';
import { CalendarEvent } from 'shovel-lib/types';

// `actionId` on each: the change being taken back, so Shovel's activity log marks it undone rather than counting it,
// and logs an Undo instead of the delete or restore it is made of.

const undoTasks = (semesterId: number, receipt: TaskUndoReceipt, actionId?: string) =>
  network.post<UndoneTask[]>(`/semesters/${semesterId}/tasks/undo`, receipt, { headers: undoOf(actionId) });

const restoreEvents = (semesterId: number, events: SourceEvent[], removeIds: string[], actionId?: string) =>
  eventsNetwork.post<SourceEvent[]>(
    `/semesters/${semesterId}/events/restore`,
    { events, removeIds },
    { headers: undoOf(actionId) }
  );

const undoPlanning = (
  semesterId: number,
  { plannedSessionIds, unplannedSessions, reviewTaskIds, places }: PlanningUndo,
  actionId?: string
) =>
  network.post<UnplanResponse>(
    `/semesters/${semesterId}/planner/undo`,
    { plannedSessionIds, unplannedSessions, reviewTaskIds, ...(places?.length ? { places } : {}) },
    { headers: undoOf(actionId) }
  );

// Taking back a session's move or stretch is an ordinary resize to where it was (shovel-lib's resizePlannedTask, which
// can't send the Undo header). `editedByStudent`: whether the student had touched it before, so undoing a move of an
// Auto-planned session leaves it Auto-planned ("Unplan Auto-planned only" still takes it); servers that don't know it
// ignore it.
const moveSessionBack = (semesterId: number, place: SessionPlace, editedByStudent: boolean, actionId?: string) =>
  network.post<CalendarEvent>(
    `/semesters/${semesterId}/planner/${place.id}/resize`,
    { newStart: place.start, newEnd: place.end, editedByStudent },
    { headers: undoOf(actionId) }
  );

// Taking back time logged on a session, or a tick taken off, is an ordinary mark-used (plannerApi.markUsed); this says it
// is an Undo.
const markSessionsUsed = (semesterId: number, blocks: MarkUsedBlock[], actionId?: string) =>
  network.post<MarkUsedResponse>(`/semesters/${semesterId}/planner/mark-used`, { blocks }, { headers: undoOf(actionId) });

// Taking back a creation is an ordinary delete on the server (shovel-lib's deleteTasks / deleteEvent); these say it
// is an Undo.
const deleteCreatedTasks = (semesterId: number, taskIds: number[], actionId?: string) =>
  network.delete(
    `/semesters/${semesterId}/tasks/delete`,
    { deleteForever: true },
    { data: taskIds, headers: undoOf(actionId) }
  );

const deleteCreatedEvent = (semesterId: number, eventId: string, query: object | undefined, actionId?: string) =>
  eventsNetwork.delete<SourceEvent[]>(`/semesters/${semesterId}/events/${eventId}`, query, { headers: undoOf(actionId) });

export const undoApi = {
  undoTasks,
  restoreEvents,
  undoPlanning,
  moveSessionBack,
  markSessionsUsed,
  deleteCreatedTasks,
  deleteCreatedEvent
};
